'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import Cookies from 'js-cookie';

const sellerLinks = [
    { label: 'Dashboard', href: '/seller/dashboard', icon: '📈' },
    { label: 'Projects', href: '/seller/projects', icon: '🏗️', builderOnly: true },
    { label: 'Ads', href: '/seller/ads', icon: '📣' },
    { label: 'My Leads', href: '/seller/leads', icon: '👥' },
    { label: 'Analytics', href: '/seller/analytics', icon: '📊' },
    { label: 'Profile', href: '/seller/profile', icon: '🆔', builderOnly: true },
];

const normalLinks = {
    sellerDashboard: [
        { label: 'Dashboard', href: '/seller/dashboard', icon: '📈' },
        { label: 'My Leads', href: '/seller/leads', icon: '👥' },
        { label: 'Analytics', href: '/seller/analytics', icon: '📊' },
        { label: 'Post Property', href: '/seller/post-property', icon: '➕' },
    ],
    myAccount: [
        { label: 'My Activity', href: '/my/activity', icon: '📊' },
        { label: 'My Profile', href: '/my/profile', icon: '👤' },
        { label: 'My Alerts', href: '/my/alerts', icon: '🔔' },
    ],
};

export default function RightDrawer({ isOpen, onClose, user, userRole, onLogout }) {
    const pathname = usePathname();
    const isSellerPage = pathname.startsWith('/seller');

    // Close on escape key
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    // Prevent body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    if (!isOpen) return null;

    const name = user?.name?.replace(/"/g, '') || '';
    const mobile = user?.mobile?.replace(/"/g, '') || '';
    const firstLetter = name.charAt(0).toUpperCase();

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm lg:hidden"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Drawer Panel */}
            <div
                className={`fixed top-0 right-0 bottom-0 z-[201] w-[280px] bg-white shadow-2xl flex flex-col lg:hidden
                    transform transition-transform duration-300 ease-out
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
                role="dialog"
                aria-modal="true"
                aria-label="User menu"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-4 bg-primary text-white shrink-0">
                    <div className="w-10 h-10 rounded-full bg-white/20 border border-white/30 flex items-center justify-center text-white font-bold text-lg shrink-0">
                        {firstLetter}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm leading-tight truncate">{name}</p>
                        <p className="text-white/70 text-xs truncate mt-0.5">{mobile}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full hover:bg-white/20 transition-colors shrink-0"
                        aria-label="Close menu"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Menu Body */}
                <div className="block overflow-y-auto py-2">
                    {isSellerPage ? (
                        /* SELLER PAGES: Seller dashboard links */
                        <div>
                            <p className="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                Seller Dashboard
                            </p>
                            {sellerLinks
                                .filter(link => !link.builderOnly || userRole === 'builder')
                                .map((link, i) => (
                                    <DrawerLink
                                        key={i}
                                        href={link.href}
                                        icon={link.icon}
                                        label={link.label}
                                        active={pathname === link.href || pathname.startsWith(link.href + '/')}
                                        onClick={onClose}
                                    />
                                ))}
                        </div>
                    ) : (
                        /* NORMAL PAGES: Seller dash links + My Account */
                        <>
                            <div>
                                <p className="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                    Seller Dashboard
                                </p>
                                {normalLinks.sellerDashboard.map((link, i) => (
                                    <DrawerLink
                                        key={i}
                                        href={link.href}
                                        icon={link.icon}
                                        label={link.label}
                                        active={pathname === link.href}
                                        onClick={onClose}
                                    />
                                ))}
                            </div>
                            <div className="mt-1 pt-1 border-t border-gray-100">
                                <p className="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                    My Account
                                </p>
                                {normalLinks.myAccount.map((link, i) => (
                                    <DrawerLink
                                        key={i}
                                        href={link.href}
                                        icon={link.icon}
                                        label={link.label}
                                        active={pathname === link.href}
                                        onClick={onClose}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer: Post Property + Logout */}
                <div className="shrink-0 border-t border-gray-100 pb-2">
                    <Link
                        href="/seller/post-property"
                        onClick={onClose}
                        className="flex items-center gap-3 mx-3 mt-2 mb-1 px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary-dark transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Post Property Free
                    </Link>
                    <button
                        onClick={() => { onClose(); onLogout(); }}
                        className="w-full flex items-center gap-3 px-7 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-semibold"
                    >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Logout
                    </button>
                </div>
            </div>
        </>
    );
}

function DrawerLink({ href, icon, label, active, onClick }) {
    return (
        <Link
            href={href}
            onClick={onClick}
            className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors
                ${active
                    ? 'bg-primary/8 text-primary border-r-2 border-primary'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                }`}
        >
            <span className="text-base w-5 text-center">{icon}</span>
            {label}
        </Link>
    );
}
